import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  TablePagination,
  InputBase,
  Button,
  Avatar,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Tabs,
  Tab,
  Menu,
} from '@mui/material';
import {
  Search,
  FileText,
  CheckCircle2,
  Clock,
  Trash2,
  Eye,
  RefreshCw,
  Phone,
  Mail,
  User,
  MapPin,
  TrendingUp,
  X,
} from 'lucide-react';
import axiosInstance from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog } from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'info', bg: '#e0f2fe', text: '#0369a1' },
  in_progress: { label: 'In Progress', color: 'warning', bg: '#fef3c7', text: '#b45309' },
  closed: { label: 'Closed', color: 'success', bg: '#dcfce7', text: '#15803d' },
  expired: { label: 'Expired', color: 'default', bg: '#f1f5f9', text: '#64748b' },
};

const RfqView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Data states
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    closed: 0,
    expired: 0,
    active: 0,
    totalQuotes: 0,
  });
  const [filterOptions, setFilterOptions] = useState({ categories: [], states: [] });

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Selected RFQ & Details Modal
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [rfqDetails, setRfqDetails] = useState(null);
  const [quotesList, setQuotesList] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);

  // Status Change Menu / State
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusTargetRfq, setStatusTargetRfq] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Delete RFQ Modal
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletingRfq, setDeletingRfq] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch RFQs list
  const fetchRfqs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        state: stateFilter !== 'all' ? stateFilter : undefined,
      };

      const res = await axiosInstance.get('/admin/rfq', { params });
      if (res.data?.success) {
        setRfqs(res.data.data || []);
        setTotalCount(res.data.total || 0);
        setStats(res.data.stats || {});
        if (res.data.filterOptions) {
          setFilterOptions(res.data.filterOptions);
        }
      }
    } catch (err) {
      console.error('Failed to fetch RFQs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, categoryFilter, stateFilter]);

  useEffect(() => {
    fetchRfqs();
  }, [fetchRfqs]);

  // Open Details Modal and load Quotes
  const handleOpenDetails = async (rfq) => {
    setSelectedRfq(rfq);
    setOpenDetailsModal(true);
    setLoadingDetails(true);
    try {
      const res = await axiosInstance.get(`/admin/rfq/${rfq._id}`);
      if (res.data?.success) {
        setRfqDetails(res.data.rfq);
        setQuotesList(res.data.quotes || []);
      }
    } catch (err) {
      console.error('Failed to load RFQ details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open Status Menu
  const handleOpenStatusMenu = (event, rfq) => {
    event.stopPropagation();
    setStatusMenuAnchor(event.currentTarget);
    setStatusTargetRfq(rfq);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusTargetRfq(null);
  };

  // Update Status
  const handleUpdateStatus = async (newStatus) => {
    if (!statusTargetRfq || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const res = await axiosInstance.patch(`/admin/rfq/${statusTargetRfq._id}/status`, {
        status: newStatus,
      });

      if (res.data?.success) {
        setRfqs((prev) =>
          prev.map((r) => (r._id === statusTargetRfq._id ? { ...r, status: newStatus } : r))
        );
        if (rfqDetails && rfqDetails._id === statusTargetRfq._id) {
          setRfqDetails((prev) => ({ ...prev, status: newStatus }));
        }
        fetchRfqs();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus(false);
      handleCloseStatusMenu();
    }
  };

  // Open Delete Dialog
  const handleOpenDelete = (rfq, e) => {
    e?.stopPropagation();
    setDeletingRfq(rfq);
    setOpenDeleteModal(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deletingRfq || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await axiosInstance.delete(`/admin/rfq/${deletingRfq._id}`);
      if (res.data?.success) {
        setOpenDeleteModal(false);
        if (openDetailsModal && selectedRfq?._id === deletingRfq._id) {
          setOpenDetailsModal(false);
        }
        fetchRfqs();
      }
    } catch (err) {
      console.error('Failed to delete RFQ:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setStateFilter('all');
    setPage(0);
  };

  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    statusFilter !== 'all' ||
    categoryFilter !== 'all' ||
    stateFilter !== 'all';

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Requests for Quote (RFQ)"
        subtitle="Review buyer procurement inquiries, track seller quote proposals, and manage deal progression."
        count={totalCount}
        countLabel="requests"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Commerce', path: '/users' },
          { label: 'RFQs' },
        ]}
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw size={16} />}
            onClick={fetchRfqs}
            disabled={loading}
          >
            Refresh
          </Button>
        }
        secondaryAction={
          <ExportTableButton
            data={rfqs}
            total={totalCount}
            onExportAll={async () => {
              const params = {
                all: true,
                search: searchTerm.trim() || undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                category: categoryFilter !== 'all' ? categoryFilter : undefined,
                state: stateFilter !== 'all' ? stateFilter : undefined,
              };
              const res = await axiosInstance.get('/admin/rfq', { params });
              return res.data?.data || [];
            }}
            columns={[
              { label: 'Title', key: 'title' },
              { label: 'Buyer Name', value: (r) => `${r.buyer_id?.first_name || ''} ${r.buyer_id?.last_name || ''}`.trim() || 'N/A' },
              { label: 'Buyer Phone', value: (r) => r.buyer_id?.phone || 'N/A' },
              { label: 'Category', value: (r) => r.category_name || r.category || 'N/A' },
              { label: 'Sub-Category', value: (r) => r.sub_category_name || r.sub_category || 'N/A' },
              { label: 'Quantity', value: (r) => `${r.quantity || ''} ${r.unit || ''}`.trim() || 'N/A' },
              { label: 'Target Price', key: 'target_price' },
              { label: 'Location', value: (r) => [r.city, r.state].filter(Boolean).join(', ') || 'N/A' },
              { label: 'Quotes Count', key: 'quotes_count' },
              { label: 'Status', key: 'status' },
              { label: 'Created At', value: (r) => r.createdAt || r.created_at ? new Date(r.createdAt || r.created_at).toLocaleString() : 'N/A' },
            ]}
            filename="rfqs"
          />
        }
      />

      {/* KPI Stats Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Total RFQs
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.5 }}>
                  {stats.total || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: 'primary.main',
                  width: 48,
                  height: 48,
                }}
              >
                <FileText size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Active (Open / In Progress)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main', mt: 0.5 }}>
                  {stats.active || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.15),
                  color: 'info.main',
                  width: 48,
                  height: 48,
                }}
              >
                <Clock size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Closed &amp; Fulfilled
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.dark', mt: 0.5 }}>
                  {stats.closed || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: 'success.dark',
                  width: 48,
                  height: 48,
                }}
              >
                <CheckCircle2 size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Seller Quotes Submitted
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.dark', mt: 0.5 }}>
                  {stats.totalQuotes || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  color: 'warning.dark',
                  width: 48,
                  height: 48,
                }}
              >
                <TrendingUp size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Card */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {/* Filter and Search Bar */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Status Tabs */}
          <Tabs
            value={statusFilter}
            onChange={(e, val) => {
              setStatusFilter(val);
              setPage(0);
            }}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.88rem',
              },
            }}
          >
            <Tab label={`All (${stats.total || 0})`} value="all" />
            <Tab
              label={
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <span>Open</span>
                  {stats.open > 0 && (
                    <Chip label={stats.open} size="small" color="info" sx={{ height: 18, fontSize: '0.75rem', fontWeight: 700 }} />
                  )}
                </Stack>
              }
              value="open"
            />
            <Tab
              label={
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <span>In Progress</span>
                  {stats.in_progress > 0 && (
                    <Chip label={stats.in_progress} size="small" color="warning" sx={{ height: 18, fontSize: '0.75rem', fontWeight: 700 }} />
                  )}
                </Stack>
              }
              value="in_progress"
            />
            <Tab label={`Closed (${stats.closed || 0})`} value="closed" />
            <Tab label={`Expired (${stats.expired || 0})`} value="expired" />
          </Tabs>

          {/* Search Box & Dropdown Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
            {/* Category Dropdown */}
            {filterOptions.categories.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }}>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2, fontSize: '0.85rem' }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {filterOptions.categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* State Dropdown */}
            {filterOptions.states.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }}>State</InputLabel>
                <Select
                  value={stateFilter}
                  label="State"
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2, fontSize: '0.85rem' }}
                >
                  <MenuItem value="all">All States</MenuItem>
                  {filterOptions.states.map((st) => (
                    <MenuItem key={st} value={st}>
                      {st}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Keyword Search */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#f5f7fa',
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                width: { xs: '100%', sm: 260 },
                border: '1px solid #e0e0e0',
              }}
            >
              <Search size={18} color="#78909c" />
              <InputBase
                placeholder="Search buyer, item, city..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                sx={{ ml: 1, flex: 1, fontSize: '0.88rem' }}
              />
            </Box>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <IconButton size="small" onClick={handleClearFilters} sx={{ color: '#ef4444' }}>
                  <X size={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* RFQ Table */}
        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: '#fafafa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Buyer Details</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Requirement / Specifications</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Quantity &amp; Target Price</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Quotes</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Created / Expires</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingSkeleton rows={rowsPerPage} columns={8} />
              ) : rfqs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0 }}>
                    <EmptyState
                      icon={FileText}
                      title="No Requests for Quote Found"
                      description={hasActiveFilters ? 'Try modifying or clearing your filters.' : 'Buyer quote requests will appear here.'}
                      actionLabel={hasActiveFilters ? 'Clear Filters' : undefined}
                      onAction={hasActiveFilters ? handleClearFilters : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rfqs.map((rfq) => {
                  const cfg = STATUS_CONFIG[rfq.status] || STATUS_CONFIG.open;
                  const isExpired = rfq.expires_at && new Date(rfq.expires_at) < new Date() && rfq.status !== 'closed';

                  return (
                    <TableRow key={rfq._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {/* Buyer */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <User size={15} color="#546e7a" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                              {rfq.buyer_name}
                            </Typography>
                          </Stack>
                          {rfq.buyer_phone && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Phone size={13} color="#78909c" />
                              <Typography
                                component="a"
                                href={`tel:${rfq.buyer_phone}`}
                                variant="caption"
                                sx={{ color: 'text.secondary', textDecoration: 'none' }}
                              >
                                {rfq.buyer_phone}
                              </Typography>
                            </Stack>
                          )}
                          {rfq.buyer_email && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Mail size={13} color="#78909c" />
                              <Typography
                                component="a"
                                href={`mailto:${rfq.buyer_email}`}
                                variant="caption"
                                sx={{ color: 'primary.main', textDecoration: 'none', maxWidth: 170, noWrap: true }}
                              >
                                {rfq.buyer_email}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Requirement */}
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                          {rfq.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 0.8,
                            lineHeight: 1.4,
                          }}
                        >
                          {rfq.description}
                        </Typography>
                        <Stack direction="row" spacing={0.8} flexWrap="wrap" gap={0.5}>
                          <Chip
                            label={rfq.category}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#f1f5f9' }}
                          />
                          {rfq.brand && (
                            <Chip
                              label={`Brand: ${rfq.brand}`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#fff7ed', color: '#c2410c' }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      {/* Quantity & Target Price */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {rfq.quantity?.toLocaleString('en-IN') || 1} units
                        </Typography>
                        <Typography variant="caption" sx={{ color: rfq.target_price ? 'success.dark' : 'text.disabled', fontWeight: 600, display: 'block' }}>
                          {rfq.target_price ? `₹${rfq.target_price.toLocaleString('en-IN')} target` : 'Flexible / Open'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          Delivery in {rfq.expected_delivery_days || 7}d
                        </Typography>
                      </TableCell>

                      {/* Location */}
                      <TableCell>
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <MapPin size={14} color="#64748b" />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                            {rfq.city}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'text.secondary', pl: 2.2, display: 'block' }}>
                          {rfq.state}
                        </Typography>
                      </TableCell>

                      {/* Quotes */}
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDetails(rfq)}
                          sx={{
                            borderRadius: '16px',
                            px: 1.5,
                            py: 0.3,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: rfq.quotes_count > 0 ? 'primary.main' : 'text.secondary',
                            borderColor: rfq.quotes_count > 0 ? 'primary.main' : '#cbd5e1',
                            bgcolor: rfq.quotes_count > 0 ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                            textTransform: 'none',
                          }}
                        >
                          {rfq.quotes_count} {rfq.quotes_count === 1 ? 'Quote' : 'Quotes'}
                        </Button>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={isExpired ? 'Expired' : cfg.label}
                          color={isExpired ? 'default' : cfg.color}
                          size="small"
                          onClick={(e) => handleOpenStatusMenu(e, rfq)}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            bgcolor: cfg.bg,
                            color: cfg.text,
                            '&:hover': { opacity: 0.9 },
                          }}
                        />
                      </TableCell>

                      {/* Created / Expires */}
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {rfq.created_at ? new Date(rfq.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          }) : '-'}
                        </Typography>
                        {rfq.expires_at && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isExpired ? 'error.main' : 'text.disabled',
                              fontWeight: isExpired ? 700 : 500,
                              display: 'block',
                            }}
                          >
                            Exp: {new Date(rfq.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.8} justifyContent="flex-end">
                          <Tooltip title="View Details & Quotes">
                            <IconButton size="small" color="primary" onClick={() => handleOpenDetails(rfq)}>
                              <Eye size={17} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete RFQ">
                            <IconButton size="small" color="error" onClick={(e) => handleOpenDelete(rfq, e)}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 1.5 } }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Status Change Context Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
        slotProps={{
          paper: { sx: { width: 170, p: 0.5 } }
        }}
      >
        <MenuItem onClick={() => handleUpdateStatus('open')} sx={{ gap: 1, borderRadius: 1 }}>
          <Chip label="Open" size="small" color="info" sx={{ height: 20 }} /> Mark as Open
        </MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('in_progress')} sx={{ gap: 1, borderRadius: 1 }}>
          <Chip label="In Progress" size="small" color="warning" sx={{ height: 20 }} /> Mark as In Progress
        </MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('closed')} sx={{ gap: 1, borderRadius: 1 }}>
          <Chip label="Closed" size="small" color="success" sx={{ height: 20 }} /> Mark as Closed
        </MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('expired')} sx={{ gap: 1, borderRadius: 1 }}>
          <Chip label="Expired" size="small" sx={{ height: 20 }} /> Mark as Expired
        </MenuItem>
      </Menu>

      {/* Details & Quotes Modal */}
      <AdminDialog
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        maxWidth="md"
      >
        <AdminDialogHeader
          title="RFQ Specifications & Proposals"
          subtitle={`ID: #${selectedRfq?._id || ''}`}
          icon={FileText}
          onClose={() => setOpenDetailsModal(false)}
        >
          {rfqDetails && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={rfqDetails.status}
                onChange={(e) => {
                  setStatusTargetRfq(rfqDetails);
                  handleUpdateStatus(e.target.value);
                }}
                sx={{ height: 32, fontSize: '0.8rem', fontWeight: 700, borderRadius: 1.5 }}
              >
                <MenuItem value="open">🟢 Open</MenuItem>
                <MenuItem value="in_progress">🟠 In Progress</MenuItem>
                <MenuItem value="closed">✅ Closed</MenuItem>
                <MenuItem value="expired">⚪ Expired</MenuItem>
              </Select>
            </FormControl>
          )}
        </AdminDialogHeader>

        <AdminDialogContent dividers>
          {loadingDetails ? (
            <Box textAlign="center" py={6}>
              <CircularProgress size={36} />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Loading RFQ details &amp; seller proposals...
              </Typography>
            </Box>
          ) : (
            rfqDetails && (
              <Stack spacing={3}>
                {/* Buyer Details Box */}
                <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                    Buyer Contact Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Name</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{rfqDetails.buyer_name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Phone</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        <a href={`tel:${rfqDetails.buyer_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          📞 {rfqDetails.buyer_phone}
                        </a>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Email</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {rfqDetails.buyer_email ? (
                          <a href={`mailto:${rfqDetails.buyer_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                            ✉️ {rfqDetails.buyer_email}
                          </a>
                        ) : (
                          'Not provided'
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Requirement Specifications */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
                    {rfqDetails.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, bgcolor: 'background.neutral', p: 2, borderRadius: 2, mb: 2 }}>
                    {rfqDetails.description}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Category</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{rfqDetails.category}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Quantity Required</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {rfqDetails.quantity?.toLocaleString('en-IN')} units
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Target Price</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                        {rfqDetails.target_price ? `₹${rfqDetails.target_price.toLocaleString('en-IN')}` : 'Open / Negotiable'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Location</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {rfqDetails.city}, {rfqDetails.state}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Quotes Section */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Seller Proposals &amp; Quotes ({quotesList.length})
                    </Typography>
                    <Chip label={`${quotesList.length} submitted`} size="small" sx={{ fontWeight: 700 }} />
                  </Stack>

                  {quotesList.length === 0 ? (
                    <Box textAlign="center" py={4} sx={{ bgcolor: 'background.neutral', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        No sellers have submitted quotes on this RFQ yet.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: 'background.neutral' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Seller</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Quoted Price</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Delivery Timeline</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Proposal Note</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {quotesList.map((quote) => {
                            const seller = quote.seller_id || {};
                            return (
                              <TableRow key={quote._id} hover>
                                <TableCell>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {seller.first_name || 'Seller'} {seller.last_name || ''}
                                  </Typography>
                                  {seller.phone && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                      📞 {seller.phone}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'success.dark' }}>
                                    ₹{quote.price?.toLocaleString('en-IN')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{quote.delivery_days || 7} days</Typography>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {quote.message || 'No additional message'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={quote.status}
                                    size="small"
                                    color={quote.status === 'accepted' ? 'success' : 'default'}
                                    sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.72rem' }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {quote.created_at ? new Date(quote.created_at).toLocaleDateString('en-IN') : '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Stack>
            )
          )}
        </AdminDialogContent>

        <AdminDialogActions>
          <Button onClick={() => setOpenDetailsModal(false)} color="inherit" sx={{ fontWeight: 600 }}>
            Close
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      <DeleteConfirmDialog
        open={openDeleteModal}
        onClose={() => !isDeleting && setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        itemName={deletingRfq?.title}
        itemType="RFQ"
        description="All associated seller proposals and bids will also be permanently removed. This action cannot be undone."
        loading={isDeleting}
      />
    </Box>
  );
};

export default RfqView;
